export type ContentBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'quote'; text: string };

export type TimelineNode = { time: string; text: string };

export type Article = {
  id: string;
  title: string;
  subtitle: string;
  excerpt: string;
  categoryId: string;
  tags: string[];
  cover: string;
  coverAlt: string;
  author: string;
  date: string;
  readTime: number;
  views: number;
  featured: boolean;
  timeline: TimelineNode[];
  content: ContentBlock[];
};

export const articles: Article[] = [
  {
    id: 'endorsement-storm-72h',
    title: '一场代言风波的时间线：从预热到公关声明只用了72小时',
    subtitle: '把散落在各平台的物料按小时拼起来，事件的骨架就清楚了',
    excerpt:
      '同一个话题，有人在讨论物料，有人在讨论立场。真正让舆论转向的往往不是最初的爆料，而是中间某个被忽略的节点。我们把72小时里的公开动作按时间排了一遍。',
    categoryId: 'timeline',
    tags: ['时间线', '公关声明', '代言合作'],
    cover:
      'https://readdy.ai/api/search-image?query=Stylized%20warm%20editorial%20illustration%20of%20a%20timeline%20made%20of%20glowing%20nodes%20across%20an%20amber%20and%20green%20melon%20field%20at%20dusk%2C%20flat%20geometric%20shapes%2C%20muted%20autumn%20tones%2C%20magazine%20cover%20aesthetic%2C%20clean%20negative%20space&width=800&height=600&seq=guatian-cover-01&orientation=landscape',
    coverAlt: '以时间线节点为造型的暖色编辑插画，用于代言风波事件复盘配图',
    author: '瓜田观察员',
    date: '2026-08-18',
    readTime: 8,
    views: 48210,
    featured: true,
    timeline: [
      { time: 'Day 1 · 10:20', text: '品牌方释出预告物料，只有剪影与一句文案，评论区开始出现猜测。' },
      { time: 'Day 1 · 19:40', text: '完整官宣上线，话题词同步进入多个平台的热门位置。' },
      { time: 'Day 2 · 09:15', text: '一条两年前的公开活动旧图被重新转发，语境被截断后开始扩散。' },
      { time: 'Day 2 · 16:30', text: '粉丝侧发布长图解释时间顺序，但传播量远低于原转发。' },
      { time: 'Day 3 · 08:05', text: '品牌方下架相关页面，未发布任何说明。' },
      { time: 'Day 3 · 21:50', text: '工作室发布声明，强调"已沟通"，事件热度随后两天内回落。' },
    ],
    content: [
      {
        type: 'p',
        text: '一场代言风波看起来是突发的，但只要把各平台的公开动作按小时排列，就会发现它其实是一条可以被拆解的流程：预热、官宣、旧料被翻出、解释、下架、声明。每个环节之间的间隔，往往比环节本身更能说明问题。',
      },
      { type: 'h2', text: '为什么第一个节点最容易被忽略' },
      {
        type: 'p',
        text: '预热物料阶段的信息量最小，也最容易被当成无关紧要的铺垫。但恰恰是这个阶段决定了后续讨论的"初始框架"：评论区会先形成一个粗略预期，之后所有新信息都会被塞进这个预期里解读。',
      },
      {
        type: 'quote',
        text: '舆论的第一判断形成得越早，后面纠正的成本就越高。这就是为什么时间线要从最早的那条动态开始排。',
      },
      { type: 'h2', text: '从旧图被翻出到官方回应之间，发生了什么' },
      {
        type: 'ul',
        items: [
          '旧图被截断语境后转发，转发量在四小时内超过原帖的二十倍。',
          '粉丝侧的解释长图发布时机偏晚，且没有同步到讨论最集中的平台。',
          '品牌方的沉默被解读成"默认"，进一步推高了情绪峰值。',
          '官方声明出现后，讨论重心从"事情本身"转向"回应态度"。',
        ],
      },
      {
        type: 'p',
        text: '整个过程中没有任何一个环节是"意外"，它们更像是标准流程在不同节奏下的排列。理解这一点，就不会轻易被单条动态牵着走：你看到的往往不是全貌，而是流程中的某一帧。',
      },
      {
        type: 'p',
        text: '最后提醒一句，时间线的价值在于顺序，而不在于立场。把所有公开动作按时间排好，结论往往自己会浮现出来，不需要额外的情绪加工。',
      },
    ],
  },
  {
    id: 'variety-editing-dispute',
    title: '综艺剪辑争议复盘：镜头分配如何影响观众的第一判断',
    subtitle: '同一段素材，剪成不同的顺序，就是两个不同的故事',
    excerpt:
      '每季综艺都会出现类似的讨论：谁被剪成了"好人"，谁被剪成了"刺头"。剪辑不是造假，但它确实会改变信息出现的顺序，而顺序决定了观众的第一判断。',
    categoryId: 'timeline',
    tags: ['综艺', '剪辑', '叙事结构'],
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20flat%20illustration%20of%20film%20strips%20and%20editing%20timeline%20on%20a%20warm%20cream%20background%20with%20orange%20and%20brick%20red%20accents%2C%20geometric%20editorial%20style%2C%20minimal%20composition%20with%20generous%20negative%20space&width=800&height=600&seq=guatian-cover-02&orientation=landscape',
    coverAlt: '暖色平面插画，用胶片与剪辑时间线表现综艺剪辑话题',
    author: '老陈',
    date: '2026-08-11',
    readTime: 6,
    views: 31240,
    featured: false,
    timeline: [
      { time: '正片上线', text: '第一期正片播出，某位嘉宾的镜头集中在争议片段。' },
      { time: '当晚', text: '社交平台出现大量截取单一镜头的讨论帖。' },
      { time: '次日', text: '另一位嘉宾的幕后花絮释出，呈现出与正片不同的相处状态。' },
      { time: '第三天', text: '节目组发布完整片段，讨论方向开始出现分化。' },
    ],
    content: [
      {
        type: 'p',
        text: '剪辑本身并不制造事实，它做的是排序。同一批素材，把矛盾的镜头放在前面、把缓和的部分放在后面，观众的第一印象就是"有冲突"；反过来排列，观感立刻不同。',
      },
      { type: 'h2', text: '三个常见的剪辑效应' },
      {
        type: 'ul',
        items: [
          '语境剥离：一段对话中抽出最短的一句，情绪强度会被成倍放大。',
          '对比剪辑：把两个人的素材交替放置，即使他们并未同处一室，也会产生对峙感。',
          '延时回收：先埋下一个模糊的表情，几十分钟后再给出一句台词，观众会自动补全因果。',
        ],
      },
      {
        type: 'p',
        text: '理解这些效应之后，再看争议就不难了。与其争论"谁对谁错"，不如先去节目组放出的完整素材里找原始顺序，很多所谓的矛盾会在完整语境里自行消解。',
      },
      {
        type: 'quote',
        text: '当一段内容被反复截取成十五秒，缺失的不只是前后文，还有它原本的情绪浓度。',
      },
      {
        type: 'p',
        text: '这也是"理性吃瓜"里最实用的一条：先找完整版，再看剪辑版。顺序一变，故事就换了主角。',
      },
    ],
  },
  {
    id: 'how-hot-search-works',
    title: '热搜是怎么上去的：话题词、主持号与流量池机制科普',
    subtitle: '热度不是凭空出现的，它有一套可以被观察的路径',
    excerpt:
      '很多人以为热搜是"自然形成"的，其实它同时受话题词设置、发布主体和时间窗口三个因素影响。理解这套机制，你就能判断一条热搜是真实讨论还是被推着走。',
    categoryId: 'industry',
    tags: ['热搜机制', '话题运营', '传播路径'],
    cover:
      'https://readdy.ai/api/search-image?query=Minimal%20abstract%20illustration%20of%20stacked%20ranking%20bars%20and%20a%20magnifier%20over%20a%20warm%20sand%20background%2C%20orange%20and%20olive%20green%20palette%2C%20flat%20editorial%20infographic%20aesthetic%2C%20clean%20lines&width=800&height=600&seq=guatian-cover-03&orientation=landscape',
    coverAlt: '沙色背景上的排行榜条形与放大镜抽象插画，表现热搜机制',
    author: '编辑部',
    date: '2026-08-05',
    readTime: 7,
    views: 56980,
    featured: true,
    timeline: [
      { time: 'T-2 小时', text: '话题词建立，由集中账号在短时间内发布同主题内容。' },
      { time: 'T-0 小时', text: '讨论量跨过入口阈值，进入榜单候选池。' },
      { time: 'T+1 小时', text: '自然用户开始参与，内容从"同质"转向"散点"。' },
      { time: 'T+6 小时', text: '若没有新素材补充，讨论量通常开始匀速下滑。' },
    ],
    content: [
      {
        type: 'p',
        text: '把热搜解构成三个变量，会清晰很多：话题词怎么写、谁先发、什么时候发。三者相互配合，才能把一条内容推到更多人面前。',
      },
      { type: 'h2', text: '变量一：话题词决定参与门槛' },
      {
        type: 'p',
        text: '一个好用的话题词通常短、具体、可以套用到很多内容上。它让原本互不相关的用户，在发布时自动共享同一个标签，讨论量因此被迅速聚合。',
      },
      { type: 'h2', text: '变量二：发布主体决定初始速度' },
      {
        type: 'ul',
        items: [
          '集中式发布：短时间内多个账号发布近似内容，快速抬高讨论基数。',
          '散点式发布：由真实用户陆续参与，增长慢但停留时间更长。',
          '接力式发布：先用集中账号起量，再让自然用户接手扩大覆盖面。',
        ],
      },
      {
        type: 'quote',
        text: '看一条热搜是否"真实"，不必判断真假，只要看它的增长曲线是不是突然出现又突然消失。',
      },
      {
        type: 'p',
        text: '需要说明的是，这套机制本身是中性的。品牌宣发、公益话题、突发新闻都可能走同样的路径。真正值得警惕的，是用它去包装一条经不起核实的说法。',
      },
    ],
  },
  {
    id: 'fan-club-organization',
    title: '粉丝后援会的组织学：应援、控评与数据打投是怎么运转的',
    subtitle: '把它当成一个没有营业执照的项目组，一切都说得通',
    excerpt:
      '后援会常被简单地理解为"一群人凑热闹"，但它其实有明确的分工、排期和考核。理解这套结构，很多看似不可理解的行为就变得可以被解释。',
    categoryId: 'industry',
    tags: ['粉丝文化', '组织分工', '应援'],
    cover:
      'https://readdy.ai/api/search-image?query=Flat%20illustration%20of%20an%20organized%20grid%20of%20small%20figures%20and%20task%20cards%20connected%20by%20lines%2C%20warm%20terracotta%20and%20cream%20palette%2C%20editorial%20geometric%20style%2C%20calm%20clean%20composition&width=800&height=600&seq=guatian-cover-04&orientation=landscape',
    coverAlt: '泰腊色平面插画，用网格小人像与任务卡表现后援会组织分工',
    author: '小 K',
    date: '2026-07-29',
    readTime: 6,
    views: 22610,
    featured: false,
    timeline: [
      { time: '筹备期', text: '确定活动目标，拆分任务模块并招募负责人。' },
      { time: '执行期', text: '按班次分配评论、转发与话题维护任务。' },
      { time: '复盘期', text: '统计各模块完成度，沉淀可复用的排期模板。' },
    ],
    content: [
      {
        type: 'p',
        text: '如果你把后援会看成一个临时项目组，它的很多行为就有迹可循了：有目标、有分工、有排期、有复盘，唯一的区别是它没有正式的雇佣关系。',
      },
      { type: 'h2', text: '典型的分工模块' },
      {
        type: 'ul',
        items: [
          '内容组：制作长图、说明文档和对外统一口径。',
          '数据组：负责话题维护、评论引导与发布节奏。',
          '资源组：协调物料、活动预算与线下应援的执行。',
          '对接组：与官方渠道沟通，确认消息的可发布边界。',
        ],
      },
      {
        type: 'p',
        text: '这套结构的高效之处在于它把"情绪"变成了"任务"，也让外部很难判断某条统一口径到底是个人观点还是组织行为。',
      },
      {
        type: 'quote',
        text: '当一群人的表达时间高度一致时，那通常不是巧合，而是排期。',
      },
      {
        type: 'p',
        text: '了解这些并不是为了嘲讽，而是为了在阅读信息时多一层判断：你看到的"众口一词"，可能只是某个小组在同一时段完成了同一份任务。',
      },
    ],
  },
  {
    id: 'old-post-archaeology',
    title: '旧帖考古的正确姿势：公开发言的时间线比截屏更可信',
    subtitle: '一张截屏说明不了什么，一条时间线可以',
    excerpt:
      '考古旧帖是很常见的操作，但单独一张截屏往往缺少语境。把公开发言按时间排列，标注当时的背景，才更接近认真核对而不是情绪输出。',
    categoryId: 'archive',
    tags: ['旧帖考古', '公开信息', '时间线'],
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20abstract%20illustration%20of%20layered%20archival%20cards%20and%20a%20vertical%20timeline%20thread%2C%20cream%20and%20amber%20palette%20with%20touches%20of%20brick%20red%2C%20flat%20editorial%20style%2C%20minimal&width=800&height=600&seq=guatian-cover-05&orientation=landscape',
    coverAlt: '米色与琥珀色平面插画，用档案卡与竖向时间线表现旧帖考古',
    author: '阿甘',
    date: '2026-07-22',
    readTime: 5,
    views: 18730,
    featured: false,
    timeline: [
      { time: '2019 年', text: '相关公开发言首次出现，语境与当时的工作内容强相关。' },
      { time: '2022 年', text: '同一账号对类似话题给出过不同表述，但背景已变化。' },
      { time: '现在', text: '两张截屏被并列转发，中间的三年被完全省略。' },
    ],
    content: [
      {
        type: 'p',
        text: '考古不一定等于揭短，它本质上是一种信息核对。问题在于，大多数流传的"考古成果"只是一张不带时间戳的截屏，语境被完全剥离。',
      },
      { type: 'h2', text: '核对公开发言时，至少要补三样东西' },
      {
        type: 'ul',
        items: [
          '时间：原始发布的时间点，以及当时的公开背景。',
          '语境：这句话是在回应什么，前后还说了什么。',
          '完整性：是否存在后续的补充说明或被忽略的另一半。',
        ],
      },
      {
        type: 'p',
        text: '把这三样补上之后，很多看似矛盾的表述其实都能被解释。真正值得追问的，从来不是"他说过什么"，而是"在什么情况下说的、后来又怎么调整的"。',
      },
      {
        type: 'quote',
        text: '截屏负责制造对比，时间线负责解释对比。前者传播得快，后者更接近事实。',
      },
      {
        type: 'p',
        text: '所以下次看到"挖出旧言论"的内容，先问一句：这张图的前后文在哪里？如果没有，那它更接近一则观点，而不是一份材料。',
      },
    ],
  },
  {
    id: 'studio-statement-template',
    title: '工作室声明模板进化史：措辞变化背后的公关思路',
    subtitle: '从"严正声明"到"感谢关注"，字里行间都是策略',
    excerpt:
      '同一种声明，措辞差几个字，公众的观感可能完全不同。梳理近年常见的声明结构，你会发现措辞其实是一套经过测试的沟通策略。',
    categoryId: 'opinion',
    tags: ['公关话术', '声明', '舆情应对'],
    cover:
      'https://readdy.ai/api/search-image?query=Flat%20abstract%20illustration%20of%20a%20document%20with%20changing%20text%20lines%20and%20speech%20bubbles%2C%20warm%20sand%20and%20olive%20green%20palette%2C%20editorial%20minimal%20style%2C%20soft%20warm%20lighting&width=800&height=600&seq=guatian-cover-06&orientation=landscape',
    coverAlt: '沙色与橄榄绿平面插画，用文件与对话框表现公关声明话术',
    author: '瓜田观察员',
    date: '2026-07-15',
    readTime: 6,
    views: 27450,
    featured: true,
    timeline: [
      { time: '早期', text: '以强硬措辞为主，强调法律追责，回应周期偏长。' },
      { time: '中期', text: '开始加入时间线与事实澄清，措辞趋于克制。' },
      { time: '近期', text: '更多使用"感谢关注""持续沟通"等柔性表述，主动压缩解读空间。' },
    ],
    content: [
      {
        type: 'p',
        text: '声明是一种时间敏感型文本。同样是回应质疑，晚发两小时，措辞就要从"事实澄清"切换到"态度致歉"，因为舆论的情绪阶段已经变了。',
      },
      { type: 'h2', text: '一份声明通常包含四个隐藏模块' },
      {
        type: 'ul',
        items: [
          '事实模块：承认什么、否认什么，边界划在哪里。',
          '时间模块：是否给出关键节点，用来证明反应速度。',
          '态度模块：对谁表达感谢或歉意，语气是强硬还是柔和。',
          '行动模块：接下来做什么，用于把讨论引导到未来而非过去。',
        ],
      },
      {
        type: 'p',
        text: '观察声明时，不必急着评价语气，先看这四个模块的配比。一份只有态度没有时间线的声明，通常意味着关键节点还没整理清楚。',
      },
      {
        type: 'quote',
        text: '公众记住的往往不是声明的内容，而是声明发出的时间。',
      },
      {
        type: 'p',
        text: '这也是为什么"回应速度"会被反复讨论：在情绪上升期，快一点比完美一点更有用。',
      },
    ],
  },
  {
    id: 'brand-contract-image-clause',
    title: '品牌解约的合同逻辑：代言合同里的形象条款长什么样',
    subtitle: '解约不是情绪决定，而是条款触发',
    excerpt:
      '品牌与艺人的合作里，"形象条款"是最容易被普通人忽略、却最常被触发的部分。理解它，就能明白很多解约声明为什么措辞如此程序化。',
    categoryId: 'industry',
    tags: ['代言合同', '形象条款', '商业合作'],
    cover:
      'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20signed%20contract%20with%20highlighted%20clauses%20and%20a%20seal%2C%20warm%20amber%20and%20brick%20red%20palette%20on%20cream%20background%2C%20editorial%20geometric%20composition&width=800&height=600&seq=guatian-cover-07&orientation=landscape',
    coverAlt: '琥珀色与砖红平面插画，用合同与印章条款表现代言合同逻辑',
    author: '编辑部',
    date: '2026-07-08',
    readTime: 7,
    views: 20380,
    featured: false,
    timeline: [
      { time: '签约期', text: '约定合作范围、期限与形象维护义务。' },
      { time: '存续期', text: '双方按季度确认物料排期与曝光节奏。' },
      { time: '触发期', text: '出现负面舆情达到条款约定标准，启动沟通程序。' },
      { time: '终止期', text: '按条款结算已执行部分，发布程式化声明。' },
    ],
    content: [
      {
        type: 'p',
        text: '大众看到的"解约"往往是一瞬间的决定，但在合同层面，它是一条被预先写好的路径：什么情况算触发、多久内要沟通、终止后如何结算，都是可以事先约定的。',
      },
      { type: 'h2', text: '形象条款通常约定这几件事' },
      {
        type: 'ul',
        items: [
          '正向义务：合作期内需维护与品牌定位相符的公开形象。',
          '负面界定：什么程度的舆情算"影响品牌形象"，通常有量化或列举标准。',
          '补救期限：触发后给多长时间用于沟通或澄清。',
          '终止后果：已支付费用的处理方式与物料下架时限。',
        ],
      },
      {
        type: 'p',
        text: '这也解释了为什么品牌方的声明高度程式化——它不是写给网友看的，而是写进合同流程里的一环，措辞必须克制、可追溯、不留额外承诺。',
      },
      {
        type: 'quote',
        text: '在商业合作里，"情绪"从来不是条款，能不能触发终止只取决于约定。',
      },
      {
        type: 'p',
        text: '对普通读者来说，记住一点就够了：解约声明里的每个字都经过斟酌，与其解读态度，不如关注它实际执行了什么动作。',
      },
    ],
  },
  {
    id: 'verify-before-repost',
    title: '网络信息核对清单：如何分辨实锤、旧照与拼接图',
    subtitle: '转发之前，花三十秒做四件事',
    excerpt:
      '很多所谓的"实锤"经不起简单核对。这里给出一份可以立刻用起来的清单，帮你判断一张图、一段视频到底经不经得起追问。',
    categoryId: 'method',
    tags: ['信息核对', '辨别方法', '理性吃瓜'],
    cover:
      'https://readdy.ai/api/search-image?query=Flat%20illustration%20of%20a%20checklist%20with%20magnifying%20glass%20and%20photo%20cards%2C%20warm%20olive%20green%20and%20amber%20palette%2C%20editorial%20minimal%20style%2C%20clean%20background&width=800&height=600&seq=guatian-cover-08&orientation=landscape',
    coverAlt: '橄榄绿与琥珀色平面插画，用核对清单与照片卡表现信息核对方法',
    author: '阿甘',
    date: '2026-06-30',
    readTime: 5,
    views: 35890,
    featured: true,
    timeline: [
      { time: '第一步', text: '找原帖，确认最早发布的时间与发布主体。' },
      { time: '第二步', text: '看完整版，判断是否经过裁剪或加速。' },
      { time: '第三步', text: '交叉核对，至少两个独立来源指向同一事实。' },
      { time: '第四步', text: '延迟转发，给信息留出被修正的时间。' },
    ],
    content: [
      {
        type: 'p',
        text: '辨别信息不需要专业工具，只需要一点耐心。下面这份清单可以直接套用在大多数传播内容上。',
      },
      { type: 'h2', text: '立刻能用的四步核对法' },
      {
        type: 'ul',
        items: [
          '找原帖：顺藤摸瓜找到最早发布的那条，而不是被转发的版本。',
          '看完整：完整视频、完整截图，特别是前后三秒和上下文对话。',
          '做交叉：如果只有单一来源在说同一件事，先默认它是待核实。',
          '缓转发：把转发延迟一天，绝大多数谣言撑不过这个时间。',
        ],
      },
      {
        type: 'p',
        text: '其中最有效的一步是"缓转发"。传播链条最需要的是速度，而速度恰恰是核对的天敌。你把速度降下来，错误的传播效率自然就下降了。',
      },
      {
        type: 'quote',
        text: '在没有确认之前，保持沉默不是冷漠，而是对事实的基本礼貌。',
      },
      {
        type: 'p',
        text: '这份清单不针对任何具体的人或事，它只是一种阅读习惯。养成之后，你会发现很多"惊天大瓜"其实在第二步就散架了。',
      },
    ],
  },
  {
    id: 'public-opinion-turn',
    title: '舆论为什么会反转：情绪峰值、新证据与沉默的多数',
    subtitle: '反转不是偶然，它通常有三个前兆',
    excerpt:
      '几乎每个热点都会经历至少一次反转。与其把反转当成"打脸"，不如把它看成一条可预测的曲线：情绪到顶、新证据出现、沉默的人开始说话。',
    categoryId: 'opinion',
    tags: ['舆论反转', '情绪曲线', '舆情分析'],
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20flat%20illustration%20of%20a%20rising%20and%20falling%20emotion%20curve%20over%20geometric%20shapes%2C%20warm%20brick%20red%20and%20cream%20palette%2C%20editorial%20minimal%20style%2C%20calm%20composition&width=800&height=600&seq=guatian-cover-09&orientation=landscape',
    coverAlt: '砖红与米色平面插画，用情绪曲线表现舆论反转现象',
    author: '小 K',
    date: '2026-06-23',
    readTime: 6,
    views: 24970,
    featured: false,
    timeline: [
      { time: '第一阶段', text: '单一叙事占主导，讨论集中在情绪表达。' },
      { time: '第二阶段', text: '出现新证据或完整版本，与初始叙事不一致。' },
      { time: '第三阶段', text: '此前沉默的群体开始发言，观点密度上升。' },
      { time: '第四阶段', text: '情绪回落，讨论转向制度性或行业性话题。' },
    ],
    content: [
      {
        type: 'p',
        text: '反转看起来是突发的，其实每个阶段都有迹可循。留意这三个前兆，就能大致判断讨论接下来会往哪走。',
      },
      { type: 'h2', text: '反转前的三个前兆' },
      {
        type: 'ul',
        items: [
          '情绪到达峰值：当讨论里几乎只剩表达而无信息时，通常说明转折快到了。',
          '出现完整版本：原帖之外的材料被放出，往往与初始叙事存在出入。',
          '沉默者发声：此前不参与讨论的相关方开始表态，补充了缺失的视角。',
        ],
      },
      {
        type: 'p',
        text: '值得注意的是，反转并不意味着原来的质疑全部错误，它更接近"信息补齐后的重新评估"。把反转当成修正，而不是打脸，讨论的质量会高很多。',
      },
      {
        type: 'quote',
        text: '每一次反转，都是信息从单声道变成立体声的过程。',
      },
      {
        type: 'p',
        text: '理解了这条曲线，你会发现追热点的正确方式不是第一时间站队，而是在每个阶段保留修正的空间。',
      },
    ],
  },
];

export default articles;